import React, { useEffect, useMemo, useState } from "react";
import { AdminCompanyExtended } from "../admin-types";
import { adminService } from "../admin.service";
import {
  Column,
  Empty,
  Panel,
  Table,
  SortConfig,
  SortDirection,
  sortData,
} from "../AdminUI";

interface Props {
  onSelectCompany: (c: AdminCompanyExtended) => void;
  cached: AdminCompanyExtended[] | null;
  onCache: (data: AdminCompanyExtended[]) => void;
  refreshKey: number;
}

export default function CompaniesList({
  onSelectCompany,
  cached,
  onCache,
  refreshKey,
}: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companies, setCompanies] = useState<AdminCompanyExtended[]>(
    cached || []
  );
  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  const handleSort = (key: string) => {
    let direction: SortDirection = "asc";

    // If clicking the same header, toggle direction
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }

    setSortConfig({ key, direction });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(q) ||
        (c.city || "").toLowerCase().includes(q) ||
        (c.country || "").toLowerCase().includes(q) ||
        (c.sector || "").toLowerCase().includes(q)
    );
  }, [companies, search]);

  const sortedRows = useMemo(() => {
    return sortData(filtered, sortConfig, {
      // Custom rule: Extract the plan name from the plan object
      plan: (company) => company.plan?.name,
    });
  }, [filtered, sortConfig]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        if (cached && refreshKey === 0) {
          setCompanies(cached);
          setLoading(false);
          return;
        }
        setLoading(true);
        const data = await adminService.fetchCompanies();
        if (!mounted) return;
        setCompanies(data || []);
        onCache(data || []);
      } catch (e: any) {
        if (mounted) setError(e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [refreshKey]);

  const cols: Column<AdminCompanyExtended>[] = [
    {
      key: "name",
      header: "Empresa",
      render: (r) => <span className="font-medium">{r.name}</span>,
      sortable: true,
    },
    {
      key: "sector",
      header: "Sector",
      sortable: true,
      render: (r) => r.sector || "—",
    },
    {
      key: "first_name",
      header: "Nombre",
      sortable: true,
      render: (r) => r.first_name || "—",
    },
    {
      key: "last_name",
      header: "Apellido",
      sortable: true,
      render: (r) => r.last_name || "—",
    },
    {
      key: "email",
      header: "Email",
      sortable: true,
      render: (r) => r.email || "—",
    },
    { key: "city", header: "Ciudad", sortable: true },
    { key: "country", header: "País", sortable: true },
    {
      key: "plan",
      header: "Plan",
      sortable: true,
      render: (r) => (r.plan && typeof r.plan === "object" ? r.plan.name : "—"),
    },
    {
      key: "created_at",
      header: "Creada",
      sortable: true,
      render: (r) =>
        r.created_at ? new Date(r.created_at).toLocaleDateString() : "—",
    },
  ];

  return (
    <Panel
      title="Empresas"
      right={
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar empresas..."
          className="input"
        />
      }
    >
      {loading ? (
        <Empty>Cargando empresas…</Empty>
      ) : error ? (
        <Empty>Error: {error}</Empty>
      ) : sortedRows.length ? (
        <Table
          columns={cols}
          rows={sortedRows}
          keyField="id"
          onRowClick={onSelectCompany}
          onSort={handleSort}
          sortConfig={sortConfig}
        />
      ) : (
        <Empty>No se encontraron empresas.</Empty>
      )}
    </Panel>
  );
}
